import produce from 'immer'
import { RacePageDTO, isRacePageDto } from '@mobi/api-types'
import { toDisplayPrice } from '@mobi/utils/money'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import type { FobPriceChangedPushEvent } from '@core/State/PushData'
import type { IObservableStarter } from '../../../../Model/Observables/IObservableStarter'
import type ObservableRacePage from '../../../../Model/Observables/ObservableRacePage'

interface PushFobPriceChanged {
  event: FobPriceChangedPushEvent
  meetingId: string
  meetingDate: Date
  raceInformation: ObservableRacePage
  raceNumber: number
  resetFobFavourite: (starters: IObservableStarter[]) => void
}

export function pushFobPriceChanged({
  event,
  raceInformation,
  meetingId,
  meetingDate,
  raceNumber,
  resetFobFavourite,
}: PushFobPriceChanged) {
  const { winPrice, placePrice, marketId, propositionId } = event

  const queryKey = queryKeys.racePageComplete(meetingId, meetingDate, raceNumber)

  // Update ReactQuery state
  queryClient.setQueryData<RacePageDTO | undefined>(queryKey, current => {
    if (!current) return undefined // key not found
    if (!isRacePageDto(current)) return undefined // only update open races
    return produce(current, draft => {
      // Extra Fixed Odds Markets
      const fixedOddsMarketMatch = draft.FixedOddsMarkets?.find(
        ({ MarketSequence }) => MarketSequence === marketId
      )
      if (fixedOddsMarketMatch) {
        const fixedPropositionWithChange = fixedOddsMarketMatch.Propositions.find(
          ({ PropositionSequence }) => PropositionSequence === propositionId
        )
        if (!fixedPropositionWithChange) return
        fixedPropositionWithChange.Price = winPrice
        fixedPropositionWithChange.PriceToDisplay = toDisplayPrice(winPrice)
        return
      }

      // Win / Place Fixed
      const starters = draft.RaceStarters.flatMap(rs =>
        rs.RaceKey.RaceNumber === raceNumber ? rs.Starters : []
      )
      for (const starter of starters) {
        if (
          starter.FixedOddsStarterInfo?.PropositionSequence === propositionId ||
          starter.FixedOddsInfo?.PropositionSequence === propositionId
        ) {
          starter.FixedOddsStarterInfo.DisplayWinDividend = toDisplayPrice(winPrice)
          starter.FixedOddsStarterInfo.DisplayPlaceDividend = toDisplayPrice(placePrice)
          starter.FixedOddsStarterInfo.IsSuspended = !winPrice

          if (starter.FixedOddsInfo) {
            starter.FixedOddsInfo.DisplayWinDividend = toDisplayPrice(winPrice)
            starter.FixedOddsInfo.DisplayPlaceDividend = toDisplayPrice(placePrice)
            starter.FixedOddsInfo.IsSuspended = !winPrice
          }
          break
        }
      }
    })
  })

  // Update KO View Model state
  const starter = raceInformation.raceStarters.getStartersByPropSeq(propositionId)

  if (starter?.fixedOddsStarterInfo && !starter.fixedOddsStarterInfo.isScratched()) {
    if (placePrice !== undefined) {
      starter.fixedOddsStarterInfo.displayPlaceDividend(toDisplayPrice(placePrice))
    }

    if (winPrice !== undefined) {
      const starters = raceInformation.getStartersForRace(raceNumber)()

      const { isSuspended, displayWinDividend, hasWinDividend } = starter.fixedOddsStarterInfo

      if (isSuspended() && !hasWinDividend()) {
        isSuspended(false)
      }

      displayWinDividend(toDisplayPrice(winPrice))
      hasWinDividend(winPrice !== null)
      resetFobFavourite(starters)
    }
  }
}
